import { RecentlyViewed } from '@theme/recently-viewed-products';

const PREDICTIVE_SEARCH_SECTION_ID = 'predictive-search';
const CARD_IMAGE_SIZES = '(min-width: 990px) 312px, 174px';
const DESCRIPTION_MAX_LENGTH = 78;
const AVAILABILITY_LABELS = {
  available: 'Disponibile',
  orderable: 'Ordinabile',
  'sold-out': 'Esaurito',
};

const normalizeText = (value) => value?.replace(/\s+/g, ' ').trim() ?? '';

const truncateText = (value, length = DESCRIPTION_MAX_LENGTH) => {
  const text = normalizeText(value);

  if (text.length <= length) return text;

  return text.slice(0, length).trimEnd();
};

const createElement = (tagName, className, textContent) => {
  const element = document.createElement(tagName);

  if (className) element.className = className;
  if (textContent !== undefined) element.textContent = textContent;

  return element;
};

const firstText = (element, selector) => normalizeText(element.querySelector(selector)?.textContent);

const getAvailabilityType = (card) => {
  if (card.dataset.exaAvailabilityType) return card.dataset.exaAvailabilityType;
  if (card.querySelector('.exa-product-card__availability .exa-tag--available')) return 'available';
  if (card.querySelector('.exa-product-card__availability .exa-tag--orderable')) return 'orderable';

  return 'sold-out';
};

class ExaRecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    const load = () => this.loadProducts();

    if ('requestIdleCallback' in window) {
      requestIdleCallback(load);
    } else {
      window.setTimeout(load, 1);
    }
  }

  async loadProducts() {
    const track = this.querySelector('[data-exa-recently-viewed-track]');
    if (!track) return;

    const limit = Number(this.dataset.limit || 8);
    const currentProductId = this.dataset.currentProductId;
    const productIds = RecentlyViewed.getProducts()
      .filter((id) => id !== currentProductId)
      .slice(0, limit);

    if (productIds.length === 0) return;

    const url = new URL(Theme.routes.search_url, window.location.origin);
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', PREDICTIVE_SEARCH_SECTION_ID);

    const response = await fetch(url.toString());
    if (!response.ok) return;

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const cards = Array.from(documentFragment.querySelectorAll('#predictive-search-products .predictive-search-results__card'));
    const exaCards = cards.slice(0, limit).map((card) => this.buildCard(card));
    const renderedCards = exaCards.filter(Boolean);

    if (renderedCards.length === 0) return;

    track.replaceChildren(...renderedCards);
    this.hidden = false;
  }

  buildCard(card) {
    const sourceLink = card.querySelector(
      '.exa-product-card__media-link[href], .exa-product-card__body-link[href], .resource-card__link[href]'
    );
    const href = sourceLink?.getAttribute('href');
    const title = firstText(card, '.exa-product-card__title, .resource-card__title');
    const url = href;

    if (!title || !url) return null;

    const comparePrice = firstText(
      card,
      '.exa-product-card__price--compare, .price-item--regular.compare-at-price, .compare-at-price'
    );
    const salePrice = firstText(card, '.exa-product-card__price--sale, .price-item--sale.price');
    const regularPrice = firstText(
      card,
      '.exa-product-card__price:not(.exa-product-card__price--compare):not(.exa-product-card__price--sale), .price__regular:not(.price__hidden) .price'
    );
    const isOnSale = Boolean(comparePrice && salePrice && comparePrice !== salePrice);
    const isNew =
      !isOnSale &&
      (card.dataset.exaIsNew === 'true' || Boolean(card.querySelector('.exa-product-card__badge .exa-tag--new')));
    const description = truncateText(card.dataset.exaCardDescription || firstText(card, '.exa-product-card__desc') || title);
    const availabilityType = getAvailabilityType(card);

    const item = createElement('li', 'exa-recently-viewed__item');
    const productCard = createElement('product-card', 'exa-product-card');
    const media = createElement('div', 'exa-product-card__media');
    const mediaLink = createElement('a', 'exa-product-card__media-link');
    const body = createElement('div', 'exa-product-card__body');
    const bodyLink = createElement('a', 'exa-product-card__body-link');
    const prices = createElement('div', 'exa-product-card__prices');

    productCard.setAttribute('data-product-transition', 'false');

    mediaLink.href = url;
    mediaLink.setAttribute('ref', 'productCardLink');
    mediaLink.setAttribute('aria-label', title);
    media.append(mediaLink);

    if (isOnSale || isNew) {
      const badge = createElement('span', 'exa-product-card__badge');
      badge.append(createElement('span', `exa-tag exa-tag--${isOnSale ? 'sale-dark' : 'new'}`, isOnSale ? 'IN OFFERTA' : 'NUOVO ARRIVO'));
      media.append(badge);
    }

    media.append(this.buildImage(card, title));

    bodyLink.href = url;
    bodyLink.append(createElement('h3', 'exa-product-card__title', title));
    bodyLink.append(createElement('p', 'exa-product-card__desc', description));
    body.append(bodyLink);

    if (isOnSale) {
      prices.append(createElement('span', 'exa-product-card__price exa-product-card__price--compare', comparePrice));
      prices.append(createElement('span', 'exa-product-card__price exa-product-card__price--sale', salePrice));
    } else {
      prices.append(createElement('span', 'exa-product-card__price', regularPrice || salePrice || comparePrice));
    }

    body.append(prices);
    body.append(this.buildAvailability(availabilityType));

    productCard.append(media, body);
    item.append(productCard);

    return item;
  }

  buildImage(card, title) {
    const sourceImage = card.querySelector(
      '.exa-product-card__image:not(.exa-product-card__image--placeholder), .resource-card__image:not(.resource-card__image--secondary), .resource-card__media img'
    );

    if (!sourceImage) {
      return createElement('div', 'exa-product-card__image exa-product-card__image--placeholder');
    }

    const image = sourceImage.cloneNode(false);
    image.className = 'exa-product-card__image';
    image.alt = image.alt || title;
    image.loading = 'lazy';
    image.sizes = CARD_IMAGE_SIZES;
    image.removeAttribute('style');

    return image;
  }

  buildAvailability(availabilityType) {
    const availability = createElement('span', 'exa-product-card__availability');
    const type = Object.prototype.hasOwnProperty.call(AVAILABILITY_LABELS, availabilityType) ? availabilityType : 'sold-out';
    const label = AVAILABILITY_LABELS[type];

    availability.append(createElement('span', `exa-tag exa-tag--${type}`, label));

    return availability;
  }
}

if (!customElements.get('exa-recently-viewed-products')) {
  customElements.define('exa-recently-viewed-products', ExaRecentlyViewedProducts);
}

document.querySelectorAll('[data-exa-recently-viewed]').forEach((section) => {
  if (section instanceof HTMLElement && section.tagName.toLowerCase() !== 'exa-recently-viewed-products') {
    const wrapper = document.createElement('exa-recently-viewed-products');
    for (const attribute of section.attributes) {
      wrapper.setAttribute(attribute.name, attribute.value);
    }
    wrapper.innerHTML = section.innerHTML;
    section.replaceWith(wrapper);
  }
});
